import { Stack } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import type { Clip } from '../clipQueueSlice';
import {
  autoplayTimeoutHandleChanged,
  selectAutoplayEnabled,
  selectAutoplayTimeoutHandle,
  selectCurrentClip,
  selectNextId,
} from '../clipQueueSlice';
import clipProvider from '../providers/providers';
import AutoplayOverlay from './AutoplayOverlay';
import VideoPlayer from './VideoPlayer';
import TwitterEmbed from './TwitterEmbed';
import TikTokEmbed from './TikTokEmbed';
import InstagramEmbed from './InstagramEmbed';

interface PlayerProps {
  className?: string;
}

const getPlayerComponent = (
  currentClip: Clip | undefined,
  videoSrc: string | undefined,
  autoplayEnabled: boolean,
  nextClipId: string | undefined,
  dispatch: ReturnType<typeof useAppDispatch>
) => {
  if (!currentClip) return null;

  const KickClip = currentClip.Platform === 'Kick';
  const TwitterClip = currentClip.Platform === 'Twitter';
  const TikTokClip = currentClip.Platform === 'TikTok';
  const InstagramClip = currentClip.Platform === 'Instagram';

  if (TwitterClip) {
    return <TwitterEmbed key={currentClip.id} url={currentClip.url} />;
  }

  if (TikTokClip) {
    // Extract the video ID from the combined ID (format: "tiktok:videoId")
    const videoId = currentClip.id.split(':')[1];
    return <TikTokEmbed key={currentClip.id} videoId={videoId} />;
  }

  if (InstagramClip) {
    // Extract the video ID from the combined ID (format: "instagram:videoId")
    const videoId = currentClip.id.split(':')[1];
    return <InstagramEmbed key={currentClip.id} videoId={videoId} />;
  }

  if (autoplayEnabled && currentClip.id) {
    return (
      <VideoPlayer
        key={`${currentClip.id}-${videoSrc}`}
        src={videoSrc}
        onEnded={() => nextClipId && dispatch(autoplayTimeoutHandleChanged({ set: true }))}
        onError={() => nextClipId && dispatch(autoplayTimeoutHandleChanged({ set: true }))}
      />
    );
  }

  if (KickClip) {
    return <VideoPlayer key={currentClip.id} src={currentClip.url} />;
  }

  const embedUrl = clipProvider.getEmbedUrl(currentClip.id);
  return (
    <iframe
      key={currentClip.id}
      src={embedUrl}
      title={currentClip.title}
      style={{ height: '100%', width: '100%' }}
      frameBorder="0"
      allow="autoplay"
      allowFullScreen
    />
  );
};

function Player({ className }: PlayerProps) {
  const dispatch = useAppDispatch();
  const currentClip = useAppSelector(selectCurrentClip);
  const nextClipId = useAppSelector(selectNextId);
  const autoplayEnabled = useAppSelector(selectAutoplayEnabled);
  const autoplayTimeoutHandle = useAppSelector(selectAutoplayTimeoutHandle);
  const [videoSrc, setVideoSrc] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentClip) {
      setVideoSrc(undefined);
      setError(null);
      return;
    }

    setVideoSrc(undefined);
    let Flag = true;

    const fetchVideoUrl = async () => {
      try {
        const url = await clipProvider.getAutoplayUrl(currentClip.id);
        if (Flag) {
          setVideoSrc(url);
          setError(null);
        }
      } catch (err) {
        if (Flag) {
          const fallbackUrl = clipProvider.getFallbackM3u8Url(currentClip.id);
          setVideoSrc(fallbackUrl);
          setError(null);
        }
      }
    };

    fetchVideoUrl();

    return () => {
      Flag = false;
    };
  }, [currentClip]);

  const player = getPlayerComponent(currentClip, videoSrc, autoplayEnabled, nextClipId, dispatch);

  return (
    <Stack
      align="center"
      sx={{ background: 'black', height: '100%', maxHeight: 'calc(100vh - 200px)', aspectRatio: '16 / 9', position: 'relative' }}
      className={className}
    >
      {error ? <div style={{ color: 'red' }}>{error}</div> : videoSrc || !autoplayEnabled ? player : <div></div>}
      <AutoplayOverlay
        visible={!!autoplayTimeoutHandle}
        onCancel={() => dispatch(autoplayTimeoutHandleChanged({ set: false }))}
      />
    </Stack>
  );
}

export default Player;
